<script setup lang="ts">
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { formatDateTime } from '@/services/functions';
import '@vuepic/vue-datepicker/dist/main.css'
import { ja } from 'date-fns/locale'
import { computed } from 'vue';
import i18n from '@/lang/i18n';
const emit = defineEmits(['update:modelValue'])

const props = defineProps<{
  modelValue: {
    splitReturningTime: string | null;
    splitLeavingTime: string | null;
  }
  deliveryPlan: getDeliveryPlanVehicleInformation;
  errorMessages: {
    returning_warehouse_date_time: string[]
    leaving_warehouse_date_time: string[]
    delivery_plan_id: string[]
  };
}>();

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
})

const splitReturningMaxDate = computed(() => {
  return modelValue.value.splitLeavingTime ?? props.deliveryPlan.returning_warehouse_date_time;
});

const splitLeavingMinDate = computed(() => {
  return modelValue.value.splitReturningTime ?? props.deliveryPlan.leaving_warehouse_date_time;
});

function changeFormat(dateTime: string | null) {
  return dateTime ? formatDateTime(new Date(dateTime)) : '';
}
</script>
<template>
  <v-col cols="2">
    <div>{{ i18n.global.t('pages.dailyReports.splitForm.leavingWarehouse') }}</div>
    <div class="w-100 d-flex mt-3">
      <div class="w-75 d-flex justify-end pr-5">
        {{ changeFormat(props.deliveryPlan.leaving_warehouse_date_time) }}
      </div>
      <div class="w-25 mx-auto">
        ～
      </div>
    </div>
  </v-col>
  <v-col cols="3" class="mr-4">
    <div>{{ i18n.global.t('pages.dailyReports.splitForm.firstHalfReturningWarehouse') }}</div>
    <ValidationErrors
      :messages="props.errorMessages.returning_warehouse_date_time"
    >
      <VueDatePicker
        class="mt-1"
        v-model="modelValue.splitReturningTime"
        :locale="ja"
        :clearable="false"
        :formats="{ input: 'yyyy-MM-dd HH:mm' }"
        :enableSeconds="false"
        :time-config="{ enableSeconds: true }"
        :min-date="props.deliveryPlan.leaving_warehouse_date_time"
        :max-date="splitReturningMaxDate"
        teleport="body"
      />
    </ValidationErrors>
  </v-col>
  <v-divider vertical class="my-4"></v-divider>
  <v-col cols="3" class="ml-4">
    <div>{{ i18n.global.t('pages.dailyReports.splitForm.secondHalfLeavingWarehouse') }}</div>
    <ValidationErrors
      :messages="props.errorMessages.leaving_warehouse_date_time"
    >
      <VueDatePicker
        class="mt-1"
        v-model="modelValue.splitLeavingTime"
        :locale="ja"
        :clearable="false"
        :formats="{ input: 'yyyy-MM-dd HH:mm' }"
        :enableSeconds="false"
        :time-config="{ enableSeconds: true }"
        :min-date="splitLeavingMinDate"
        :max-date="props.deliveryPlan.returning_warehouse_date_time"
        teleport="body"
      />
    </ValidationErrors>
  </v-col>
  <v-col cols="2">
    <div>{{ i18n.global.t('pages.dailyReports.splitForm.returningWarehouse') }}</div>
    <div class="w-100 d-flex mt-3">
      <div class="w-25 mx-auto">
        ～
      </div>
      <div class="w-75 d-flex justify-start pl-5">
        {{ changeFormat(props.deliveryPlan.returning_warehouse_date_time) }}
      </div>
    </div>
  </v-col>
</template>
